import CatalogSkeleton from 'components/CatalogSkeleton'
import VideoList from 'components/VideoList'
import CustomTablePagination from 'components/CustomTablePagination'
import { CoreContext } from 'context/CoreContext'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { ReadPortalContent, ReadPortalHomeCourses } from 'services/portalContent'
import { SaveObject } from 'services/storage'
import useI18n from 'hooks/useI18n'
import { adapters } from 'utils/adapters'
import { apiRequest, exposeStrapiError } from 'utils'
import { languages } from 'utils/options'
import { PaginationContainer } from 'screens/AuthenticatedArea/SubCategory/styled'
import { useAuthLayout } from 'context/AuthLayoutContext'
import { FormSpacer } from 'ui/styled'

const DEFAULT_RENDER_OPTION = 'gallery'

const normalizeRawCategories = (item) => {
  if (!item?.raw_category) return []
  return Array.isArray(item.raw_category) ? item.raw_category : [item.raw_category]
}

const normalizeSubCategories = (item) => {
  if (!item?.sub_categories) return []
  return Array.isArray(item.sub_categories) ? item.sub_categories : [item.sub_categories]
}

export default function MVUEssentialsSubCategory() {
  const { id, type } = useParams()
  const location = useLocation()
  const params = new URLSearchParams(location?.search)
  const subId = params.get('q')

  const { t } = useI18n()
  const { setFiltered, language } = useContext(CoreContext)
  const { setLayoutProps } = useAuthLayout()

  const [register, setRegister] = useState({ data: [] })
  const [courseRegisters, setCourseRegisters] = useState([])
  const [loadingCourses, setLoadingCourses] = useState(false)
  const [pageSize] = useState(50)
  const [page, setPage] = useState(0)
  const [pageCount, setPageCount] = useState(0)
  const [total, setTotal] = useState(0)
  const [legacyLoading, setLegacyLoading] = useState(false)
  const [renderOption, setRenderOption] = useState(DEFAULT_RENDER_OPTION)

  const locale = useMemo(() => languages.find((item) => item.id === language)?.ref || 'en', [language])
  const courseQuery = useMemo(() => {
    return {
      categoryId: id,
      subCategoryId: subId,
      page: page + 1,
      pageSize,
    }
  }, [id, subId, page, pageSize])

  const legacyContentType = useMemo(() => `${type || ''}`.replace(/-/g, '_'), [type])

  const loadCourses = useCallback(async () => {
    if (type !== 'courses') return

    setLoadingCourses(true)
    const result = await apiRequest(() => ReadPortalHomeCourses('essentials', courseQuery))

    if (!exposeStrapiError(result)) {
      const items = Array.isArray(result?.data) ? result.data.map((item) => adapters.courses(item, locale)) : []
      const pagination = result?.meta?.pagination || {}

      setCourseRegisters(items)
      setPageCount(pagination?.pageCount || 0)
      setTotal(pagination?.total || 0)
    }

    setLoadingCourses(false)
  }, [courseQuery, locale, type])

  const handleLegacyContent = useCallback(async () => {
    setLegacyLoading(true)
    const results = await ReadPortalContent('essentials', legacyContentType, locale)
    if (!exposeStrapiError(results)) {
      const adapterByContentType = {
        marketing_materials: adapters['marketing-materials'],
        support_materials: adapters['support-materials'],
      }
      const contentAdapter = adapterByContentType[legacyContentType]

      setRegister({
        ...results,
        data: Array.isArray(results?.data) && contentAdapter
          ? results.data.map((item) => contentAdapter(item, locale))
          : results?.data,
      })
    }
    setLegacyLoading(false)
  }, [legacyContentType, locale])

  useEffect(() => {
    if (type === 'courses') {
      loadCourses()
    }
  }, [type, loadCourses])

  useEffect(() => {
    if (type !== 'courses') {
      handleLegacyContent()
    }
  }, [type, handleLegacyContent])

  useEffect(() => {
    setPage(0)
  }, [id, subId, type])

  useEffect(() => {
    setLayoutProps({})
  }, [setLayoutProps])

  useEffect(() => {
    setFiltered(null)
    SaveObject('filtered', null)
  }, [location, setFiltered])

  const courseViewModel = useMemo(() => {
    if (type !== 'courses') {
      return {
        activeCategory: null,
        items: [],
      }
    }

    const items = Array.isArray(courseRegisters) ? [...courseRegisters] : []
    const categoryEntries = items.flatMap((item) => normalizeRawCategories(item))

    const activeCategory =
      categoryEntries.find((category) => `${category?.id}` === `${id}`) ||
      categoryEntries[0] ||
      null

    const selectedItems = items
      .filter((item) => {
        if (!activeCategory?.id) return true
        return normalizeRawCategories(item).some((category) => `${category?.id}` === `${activeCategory.id}`)
      })
      .sort((a, b) => {
        if (!a?.order) return -b?.order
        if (!b?.order) return -a?.order
        return (a?.order || 0) - (b?.order || 0)
      })

    return {
      activeCategory,
      items: selectedItems,
    }
  }, [courseRegisters, id, type])

  useEffect(() => {
    if (type === 'courses') {
      setRenderOption(DEFAULT_RENDER_OPTION)
    }
  }, [type])

  const categories = useMemo(() => {
    if (type !== 'courses') return []
    const selectedCategoryTitle = courseViewModel?.activeCategory?.title || ''
    const selectedSubCategoryTitle = subId
      ? (courseViewModel?.items || [])
        .flatMap((item) => normalizeSubCategories(item))
        .find((subCategory) => `${subCategory?.id}` === `${subId}`)?.title || ''
      : ''
    const titleParts = [t('courses'), selectedCategoryTitle, selectedSubCategoryTitle].filter(Boolean)
    const rootPath = '/mvu-lite/details/courses'
    const categoryPath = id ? `/mvu-lite/details/courses/${id}` : rootPath
    const breadcrumbs = [
      { label: t('home'), icon: 'home', to: '/mvu-lite' },
      { label: t('courses'), to: selectedCategoryTitle ? rootPath : null, primary: true },
      selectedCategoryTitle ? { label: selectedCategoryTitle, to: selectedSubCategoryTitle ? categoryPath : null } : null,
      selectedSubCategoryTitle ? { label: selectedSubCategoryTitle, to: null } : null,
    ].filter(Boolean)

    return [
      {
        section: t('courses_available_for_you'),
        title: titleParts.length > 1 ? `${titleParts[0]} <small>> ${titleParts.slice(1).join(' > ')}</small>` : titleParts[0],
        breadcrumbs,
        data: courseViewModel?.items || [],
        type: 'courses',
      }
    ]
  }, [courseViewModel, id, subId, t, type])

  const onPageChange = (e, nextPage) => setPage(nextPage)

  return (
    <>
      <FormSpacer extraLarge />
      {
        (type === 'courses' ? loadingCourses : legacyLoading) ? (
          <CatalogSkeleton />
        ) : (
          <>
            {type === 'courses' ? (
              <>
                {categories?.map((data) => (
                  <VideoList
                    key={`mvu-${data?.type || 'courses'}-${id || 'all'}`}
                    listOption
                    portal="essentials"
                    setRenderOption={setRenderOption}
                    renderOption={renderOption}
                    data={data}
                    wide
                    label={t('filters')}
                    nobutton
                    noShare
                    enablePdfPreview
                  />
                ))}
                {pageCount === 1 ? null : (
                  <PaginationContainer>
                    <CustomTablePagination count={total} page={page} rowsPerPage={pageSize} onPageChange={onPageChange} />
                  </PaginationContainer>
                )}
              </>
            ) : (
              <VideoList
                listOption
                portal="essentials"
                setRenderOption={setRenderOption}
                renderOption={renderOption}
                data={register}
                wide
                label={t('filters')}
                nobutton
                noShare
                enablePdfPreview
              />
            )}
          </>
        )
      }
    </>

  )
}
